import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/system';
import { Grid, Box, Typography, Button, Menu, MenuItem, Divider } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { applyColumnStyle, handleDeviceColumns } from 'utils/commonHelpers';
import MainCard from 'components/Cards/MainCard';
import GroupsTreeView from 'components/Management/DevicesFilter';
import ApiHelper from 'utils/apiHelper';
import EditDeviceColumnsVisibilityDialog from './EditDeviceColumnsVisibility';
import RightClickOptionsDialog from './RightClickOptionsDialog';
import ReportFileExports from 'components/Reports/FileExports';
import { getDevices, updateSearchParams } from 'components/../redux/actions/devices';
import { putSettings } from 'components/../redux/actions/settings';
import { API_URL } from '../../../utils/constants';

const ManagementDevices = () => {
    const theme = useTheme();
    const gridRef = useRef();
    const [csvDownloadFileHeaders, setCsvDownloadFileHeaders] = useState([]);
    const { userInfo } = useSelector((state) => state.auth);
    const [groupsData, setGroupsData] = useState({});
    const [deviceColumnsData, setDeviceColumnsData] = useState([]);
    const [totalDeviceColumns, setTotalDeviceColumns] = useState([]);
    const [showEditDeviceOrderColumns, setShowEditDeviceOrderColumns] = useState(false);
    const [columnDefs, setColumnDefs] = useState([]);
    const [showContextMenuDialog, setShowContextMenuDialog] = useState(false);
    const [rightClickOption, setRightClickOption] = useState('');
    const [selectedDevicesData, setSelectedDevicesData] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(0);
    const [contextMenu, setContextMenu] = useState(null);
    const [queryParams, setQueryParams] = useState({});
    const [sortModel, setSortModel] = useState([]);
    const settings = useSelector((state) => state.settings);
    const [isScriptAvailable, setScriptAvailable] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: settings?.generalSettings?.showRows ? settings?.generalSettings?.showRows : 25
    });
    const devices = useSelector((state) => state.devices);
    const dispatch = useDispatch();
    const [limitLength, setLimitLength] = useState();

    useEffect(() => {
        getScriptList();
        getDeviceColumns();
        getGroups();
        const params = {
            keyword: '',
            id: 0,
            sort: '',
            field: '',
            page_size: settings?.generalSettings?.showRows ? settings?.generalSettings?.showRows : 25,
            page_number: 0
        };
        setQueryParams(params);
    }, [settings?.generalSettings?.showRows]);

    useEffect(() => {
        if (Object.keys(queryParams).length > 0) {
            dispatch(updateSearchParams(queryParams));
            dispatch(getDevices(queryParams));
        }
    }, [dispatch, queryParams]);

    const updatePaginationModel = (newPaginationModel) => {
        const params = {
            id: selectedGroupId,
            sort: sortModel.length > 0 ? sortModel[0].sort : '',
            field: sortModel.length > 0 ? sortModel[0].field : '',
            page_size: newPaginationModel.pageSize,
            page_number: newPaginationModel.page
        };
        setQueryParams(params);
        //dispatch(updateSearchParams(params));
        //dispatch(getDevices(params));
        if (userInfo.admin_permissions.org_admin) {
            dispatch(
                putSettings(
                    {
                        ...settings,
                        generalSettings: {
                            ...settings.generalSettings,
                            showRows: newPaginationModel.pageSize
                        }
                    },
                    false
                )
            );
        }
        setPaginationModel(newPaginationModel);
    };
    const getGroups = () => {
        ApiHelper.get(API_URL.MANAGEMENT_GROUPS)
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the groups data');
                }
            });
    };

    const getDeviceColumns = () => {
        ApiHelper.get('/api/management/device_columns/1/')
            .then((res) => {
                setTotalDeviceColumns(res.data.devices);
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data.devices);
                setLimitLength(res.data.limit_length);
                setColumnDefs(applyColumnStyle(columns_order));
                setDeviceColumnsData(columns_visibility);
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.warn('There is device columns data');
                }
            });
    };

    const getScriptList = () => {
        ApiHelper.get(API_URL.MANAGEMENT_SCRIPTS)
            .then((res) => {
                setScriptAvailable(res.data.script_list.length > 0);
            })
            .catch(() => {
                setScriptAvailable(false);
            });
    };

    const handleFilterDevicesByGroupId = (id) => {
        if (id !== selectedGroupId) {
            setSelectedGroupId(id);
            const params = {
                id,
                field: '',
                sort: '',
                page_size: paginationModel.pageSize,
                page_number: paginationModel.page
            };
            setQueryParams(params);
            dispatch(updateSearchParams(params));
            dispatch(getDevices(params));
        }
    };

    const handleCloseEditDeviceColumnsDialog = () => {
        setShowEditDeviceOrderColumns(false);
    };

    const handleCloseRightClickOptionsDialog = (isCancel = true) => {
        setShowContextMenuDialog(false);
        if (!isCancel) {
            const params = {
                id: selectedGroupId,
                field: sortModel.length > 0 ? sortModel[0].field : '',
                sort: sortModel.length > 0 ? sortModel[0].sort : '',
                page_size: paginationModel.pageSize,
                page_number: paginationModel.page
            };
            setQueryParams(params);
            // dispatch(updateSearchParams(params));
            // dispatch(getDevices(params));
        }
    };

    const handleSubmitDeviceColumnsVisibility = (data) => {
        setShowEditDeviceOrderColumns(false);
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_column_visibility',
            data: data
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(applyColumnStyle(columns_order));
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully updated.');
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch(() => {
                toast.error('Failed to update the device columns.');
            });
    };

    const handleChangeColumnOrder = (data) => {
        const { targetIndex, oldIndex } = data;
        const temp = columnDefs;
        const updatedVal = temp[oldIndex - 1];
        temp.splice(oldIndex - 1, 1);
        temp.splice(targetIndex - 1, 0, updatedVal);
        setColumnDefs(applyColumnStyle(temp));
        const headers = [];
        temp.map((data) => {
            headers.push({ label: data.headerName, key: data.field });
        });
        setCsvDownloadFileHeaders(headers);
    };

    const handleSaveColumnOrders = () => {
        const columns_order = {};
        for (let i = 0; i < columnDefs.length; i++) {
            columns_order[columnDefs[i].field] = i + 1;
        }
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_columns_order',
            data: columns_order
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(
                    applyColumnStyle(
                        columns_order.map((c) => ({
                            ...c,
                            resizable: true,
                            headerClassName: 'device-column-header',
                            cellClassName: 'wrapCell'
                        }))
                    )
                );
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully saved.');
            })
            .catch(() => {
                toast.error('Failed to save the columns order');
            });
    };

    const onClickContextMenu = (e) => {
        setShowContextMenuDialog(true);
        handleCloseContextMenu();
        setRightClickOption(e.target.id);
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
        if (selectedDevicesData.length === 0) {
            toast.warning('Please select the device');
            return true;
        }
        setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 } : null);
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleChangeRowSelection = (ids) => {
        const selectedIDs = new Set(ids);
        const selectedRows = devices.data.filter((row) => selectedIDs.has(row.deviceid));
        setSelectedDevicesData(selectedRows);
    };

    const handleSortModelChange = React.useCallback(
        (newSortModel) => {
            //console.log(newSortModel);
            setSortModel(newSortModel);
            if (newSortModel.length > 0) {
                const params = {
                    id: selectedGroupId,
                    sort: newSortModel[0].sort,
                    field: newSortModel[0].field,
                    page_size: paginationModel.pageSize,
                    page_number: 0
                };
                setPaginationModel({ pageSize: paginationModel.pageSize, page: 0 });
                setQueryParams(params);
            }
        },
        [paginationModel, selectedGroupId]
    );

    if (!devices || !devices.data || !deviceColumnsData || !groupsData) {
        return null;
    } else {
        return (
            <MainCard title={`Device Management - ${groupsData['organization_data']?.name}`} cardHeaderShow={false}>
                <Grid container>
                    {devices?.total > 0 && (
                        <Grid item xs={12} sx={{ textAlign: 'right' }}>
                            <Typography>{`Showing ${devices?.data.length} out of ${devices?.total} total devices `}</Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={3} md={3}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>Managed Systems</Typography>
                            </Grid>
                            <Grid item sm={9} md={9} sx={{ textAlign: 'right' }}>
                                <Button
                                    sx={{ marginRight: '5px', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={handleSaveColumnOrders}
                                >
                                    Save Column Order
                                </Button>
                                <Button
                                    sx={{ marginRight: '5px', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowEditDeviceOrderColumns(true)}
                                >
                                    Show/Hide Columns
                                </Button>
                                <ReportFileExports
                                    elementId="devices"
                                    downloadFileName="devices"
                                    downloadFileOption="csv"
                                    data={devices.data}
                                    headers={csvDownloadFileHeaders}
                                />
                                {showEditDeviceOrderColumns && (
                                    <EditDeviceColumnsVisibilityDialog
                                        data={deviceColumnsData}
                                        onClose={handleCloseEditDeviceColumnsDialog}
                                        onSubmit={handleSubmitDeviceColumnsVisibility}
                                        columeData={totalDeviceColumns}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={2} md={3} sm={3} xs={12} sx={{ background: 'linear-gradient(to right, #fcc067, #f27d00)' }}>
                                <GroupsTreeView groupsData={groupsData} handleFilterGroups={handleFilterDevicesByGroupId} />
                            </Grid>
                            <Grid item lg={10} md={9} sm={9} xs={12}>
                                {devices.loading === true ? (
                                    <Box
                                        id="devices"
                                        sx={{
                                            fontSize: '12px',
                                            '& .device-column-header': {
                                                color: 'black',
                                                fontWeight: 'bold'
                                            },
                                            height: 'calc(100vh - 306px)',
                                            width: '100%'
                                        }}
                                    >
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    </Box>
                                ) : !devices?.data || devices?.data?.length === 0 ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center' }}>There is no devices data</Typography>
                                ) : (
                                    <Box
                                        id="devices"
                                        sx={{
                                            fontSize: '12px',
                                            '& .device-column-header': {
                                                color: 'black',
                                                fontWeight: 'bold'
                                            },
                                            height: 'calc(100vh - 282px)',
                                            width: '100%'
                                        }}
                                    >
                                        <DataGridPro
                                            columns={columnDefs}
                                            rows={devices.data}
                                            rowCount={devices.total}
                                            rowHeight={30}
                                            ref={gridRef}
                                            getRowId={(row) => row.deviceid}
                                            pagination
                                            initialState={{
                                                pagination: {
                                                    paginationModel
                                                }
                                            }}
                                            pageSizeOptions={[25, 100, 300, 500, 1000]}
                                            checkboxSelection
                                            disableColumnMenu
                                            onColumnOrderChange={handleChangeColumnOrder}
                                            slotProps={{
                                                row: {
                                                    onContextMenu: handleContextMenu,
                                                    style: { cursor: 'context-menu' }
                                                },
                                                pagination: {
                                                    showFirstButton: true,
                                                    showLastButton: true
                                                }
                                            }}
                                            onRowSelectionModelChange={handleChangeRowSelection}
                                            sortingMode="server"
                                            sortModel={sortModel}
                                            paginationMode={'server'}
                                            onSortModelChange={handleSortModelChange}
                                            loading={devices.loading}
                                            onPaginationModelChange={updatePaginationModel}
                                            disableRowHeightAdjustment={false}
                                            getRowHeight={() => 30}
                                            sx={{
                                                '& .MuiDataGrid-columnHeaders': {
                                                    backgroundColor: '#f5f5f5', // Optional: Customize header background
                                                    position: 'sticky', // Ensures headers are sticky
                                                    top: 0,
                                                    zIndex: 1 // Ensures headers stay above content when scrolling
                                                },
                                                '& .MuiDataGrid-virtualScroller': {
                                                    overflowY: 'auto',
                                                    overflowX: 'auto' // Enable horizontal scrolling
                                                },
                                                '& .MuiDataGrid-columnHeaderTitleContainer': {
                                                    display: 'flex',
                                                    justifyContent: 'center', // Center text horizontally
                                                    alignItems: 'center', // Center text vertically
                                                    height: '100%'
                                                }
                                            }}
                                        />
                                        <Menu
                                            open={contextMenu !== null}
                                            onClose={handleCloseContextMenu}
                                            anchorReference="anchorPosition"
                                            anchorPosition={
                                                contextMenu !== null
                                                    ? {
                                                          top: contextMenu.mouseY,
                                                          left: contextMenu.mouseX
                                                      }
                                                    : undefined
                                            }
                                            componentsProps={{
                                                root: {
                                                    onContextMenu: (e) => {
                                                        e.preventDefault();
                                                        handleCloseContextMenu();
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem id="wake" onClick={onClickContextMenu}>
                                                Wake
                                            </MenuItem>
                                            <MenuItem id="sleep" onClick={onClickContextMenu}>
                                                Sleep
                                            </MenuItem>
                                            <MenuItem id="restart" onClick={onClickContextMenu}>
                                                Restart
                                            </MenuItem>
                                            <MenuItem id="shutdown" onClick={onClickContextMenu}>
                                                ShutDown
                                            </MenuItem>
                                            <MenuItem id="group" onClick={onClickContextMenu}>
                                                Assign Group
                                            </MenuItem>
                                            <MenuItem id="policy" onClick={onClickContextMenu}>
                                                Assign Policy
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem id="label" onClick={onClickContextMenu}>
                                                Assign Label
                                            </MenuItem>
                                            {userInfo.admin_permissions.root_admin && (
                                                <MenuItem id="setBaseline" onClick={onClickContextMenu}>
                                                    Set Baseline
                                                </MenuItem>
                                            )}
                                            <Divider />
                                            {userInfo.admin_permissions.root_admin && (
                                                <MenuItem id="getLogFile" onClick={onClickContextMenu}>
                                                    Get Log File
                                                </MenuItem>
                                            )}
                                            <MenuItem id="upgrade" onClick={onClickContextMenu}>
                                                Upgrade/No Upgrade
                                            </MenuItem>
                                            <MenuItem id="license" onClick={onClickContextMenu}>
                                                Licensing
                                            </MenuItem>
                                            {userInfo.admin_permissions.org_admin && (
                                                <MenuItem id="getTISData" onClick={onClickContextMenu}>
                                                    Get TIS Data
                                                </MenuItem>
                                            )}
                                            {userInfo.admin_permissions.org_admin && isScriptAvailable && (
                                                <MenuItem id="script" onClick={onClickContextMenu}>
                                                    Run Script
                                                </MenuItem>
                                            )}
                                        </Menu>
                                    </Box>
                                )}
                                {showContextMenuDialog && (
                                    <RightClickOptionsDialog
                                        option={rightClickOption}
                                        onClose={handleCloseRightClickOptionsDialog}
                                        devices={selectedDevicesData}
                                        inputFields={limitLength}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementDevices;
